import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  IconButton,
  Alert,
  Grid,
  InputAdornment,
  CircularProgress,
  Chip,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Link,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Payment as PaymentIcon,
  AccountBalance as BankIcon,
  ConfirmationNumber as TicketIcon,
  CalendarToday as DateIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  ExpandMore as ExpandMoreIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { getTimezoneAbbreviation } from '../../utils/dateUtils';
import StripeCheckout from '../common/StripeCheckout';
import { validateEmail } from '../../utils/validators';
import parse from 'html-react-parser';
import { getCurrencySymbol } from '../../utils/currencyUtils';

const EventRegistrationModal = ({ event, open, onClose }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    ticketType: '',
    quantity: 1,
    paymentMethod: 'card',
    selectedScheduleId: null,
    agreeToTerms: false
  });
  const [errors, setErrors] = useState({});
  const [processing, setProcessing] = useState(false);
  const [purchaseResponse, setPurchaseResponse] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState('');
  const [fetchingAccounts, setFetchingAccounts] = useState(false);
  const [finalSelectedTicket, setFinalSelectedTicket] = useState(null);

  // Stripe payment state
  const stripeCheckoutRef = useRef();
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [creatingPaymentIntent, setCreatingPaymentIntent] = useState(false);
  const [cardPaymentReady, setCardPaymentReady] = useState(false);


  // Fetch bank accounts when payment method changes to bank
  useEffect(() => {
    const fetchBankAccounts = async () => {
      if (formData.paymentMethod !== 'bank_account' || !open) return;

      setFetchingAccounts(true);
      try {
        const response = await api.get('/v1/api/users/user/stripe-bank-accounts');
        setBankAccounts(response.data);
        if (response.data.length > 0) {
          setSelectedBankAccount(response.data[0].id);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          setErrors({ general: 'Failed to load bank accounts' });
        }
      } finally {
        setFetchingAccounts(false);
      }
    };

    fetchBankAccounts();
  }, [formData.paymentMethod, open]);

  // Get selected ticket details
  const selectedTicket = useMemo(() => {
    if (!formData.ticketType) return null;
    return event?.eventTickets?.find(t => t.id == formData.ticketType);
  }, [formData.ticketType, event]);

  // Check if selected ticket is free
  const isSelectedTicketFree = useMemo(() => {
    return finalSelectedTicket && finalSelectedTicket.price === 0;
  }, [finalSelectedTicket]);

  // Get selected schedule details
  const selectedSchedule = useMemo(() => {
    if (!formData.selectedScheduleId) return null;
    return event?.schedules?.find(s => s.id == formData.selectedScheduleId);
  }, [formData.selectedScheduleId, event]);

  // Adjust steps based on whether selected ticket is free
  const steps = useMemo(() => {
    if (isSelectedTicketFree) {
      return ['Registration Info', 'Confirmation'];
    }
    return ['Registration Info', 'Payment', 'Confirmation'];
  }, [isSelectedTicketFree]);

  useEffect(() => {
    if (open) {
      resetForm();

      // Force select current schedule when modal opens
      if (event?.schedules?.length > 0) {
        const currentSchedule = event.schedules.find(schedule => schedule.isCurrent);
        const firstSchedule = event.schedules[0];

        setFormData(prev => ({
          ...prev,
          selectedScheduleId: currentSchedule ? currentSchedule.id : firstSchedule.id
        }));
      }
    }
  }, [open, event]);

  useEffect(() => {
    if (!open) {
      // Reset payment state when dialog closes
      setClientSecret(null);
      setPaymentIntentId('');
      setCreatingPaymentIntent(false);
      setCardPaymentReady(false);
    }
  }, [open]);

  // Reset payment method to card if currency is not USD
  useEffect(() => {
    const isUSD = user?.defaultCurrency?.toUpperCase() === 'USD';
    if (!isUSD && formData.paymentMethod === 'bank_account') {
      setFormData(prev => ({ ...prev, paymentMethod: 'card' }));
    }
  }, [user?.defaultCurrency, formData.paymentMethod]);

  useEffect(() => {
    // Auto-select the current schedule if available, otherwise first schedule
    if (event?.schedules?.length > 0 && !formData.selectedScheduleId) {
      const currentSchedule = event.schedules.find(schedule => schedule.isCurrent);
      const firstSchedule = event.schedules[0];

      setFormData(prev => ({
        ...prev,
        selectedScheduleId: currentSchedule ? currentSchedule.id : firstSchedule.id
      }));
    }
  }, [event?.schedules, formData.selectedScheduleId, open]);

  useEffect(() => {
    // When ticket type changes, update quantity to match minOrder
    if (finalSelectedTicket) {
      setFormData(prev => ({
        ...prev,
        quantity: Math.max(prev.quantity, finalSelectedTicket.minOrder)
      }));
    }
  }, [finalSelectedTicket]);



  useEffect(() => {
    // Only create payment intent for paid tickets with card payment
    if (formData.paymentMethod === 'card' && !isSelectedTicketFree && formData.ticketType && formData.quantity > 0) {
      createPaymentIntent();
    }
  }, [formData.paymentMethod, formData.ticketType, formData.quantity, formData.email, isSelectedTicketFree]);

  const resetForm = () => {
    const currentSchedule = event?.schedules?.find(schedule => schedule.isCurrent);
    const firstSchedule = event?.schedules?.[0];

    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phoneNumber || '',
      ticketType: '',
      quantity: 1,
      paymentMethod: 'card',
      selectedScheduleId: currentSchedule ? currentSchedule.id : firstSchedule?.id || null,
      agreeToTerms: false
    });
    setErrors({});
    setActiveStep(0);
    setProcessing(false);
    setClientSecret(null);
    setPaymentIntentId('');
    setFinalSelectedTicket(null);
    setBankAccounts([]);
    setSelectedBankAccount('');
  };

  const createPaymentIntent = async () => {
    if (!validateEmail(formData.email)) {
      setErrors({ email: 'Valid email required for payment' });
      return;
    }
    if (!selectedSchedule?.tickets) {
      return;
    }

    try {
      setCreatingPaymentIntent(true);
      setErrors({});

      // Amount in major currency (price is tax inclusive)
      const amount = (finalSelectedTicket?.price || 0) * formData.quantity;

      const response = await api.post('/v1/api/payment/create-payment-intent', {
        amount: amount,
        email: formData.email,
        currency: event.currency,
        targetId: event.uuid,
        purpose: 'EVENT_TICKET_PURCHASE',
        description: `Event registration: ${event.title} - ${finalSelectedTicket?.name}`
      });

      setClientSecret(response.data.clientSecret);
      setPaymentIntentId(response.data.pi);
    } catch (error) {
      console.error('Payment intent creation failed:', error);
      setErrors({
        general: error.response?.data?.error || 'Payment initialization failed'
      });
    } finally {
      setCreatingPaymentIntent(false);
    }
  };



  const handleNext = () => {
    if (activeStep === 0) {
      const newErrors = validateForm();
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      // If selected ticket is free, skip payment step and submit directly
      if (isSelectedTicketFree) {
        handleSubmit(); // Automatically submit for free tickets
        return;
      }
    }

    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    // If going back from confirmation and ticket is free, go back to registration info
    if (activeStep === 1 && isSelectedTicketFree) {
      setActiveStep(0);
    } else {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle checkbox differently from other inputs
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));

      if (errors[name]) {
        setErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
      return;
    }

    // For quantity field, ensure it's a number and within allowed range
    let processedValue = value;
    if (name === 'ticketType') {
      processedValue = parseInt(value, 10); // Convert to number
      const selectedTicket = selectedSchedule?.tickets?.find(t => t.id === processedValue);
      setFinalSelectedTicket(selectedTicket);

    } else if (name === 'quantity') {
      const numValue = parseInt(value, 10) || 0;
      if (finalSelectedTicket) {
        // Enforce min and max order constraints, respecting available tickets
        const effectiveMax = Math.min(
          finalSelectedTicket.maxOrder,
          finalSelectedTicket.available || finalSelectedTicket.maxOrder
        );
        processedValue = Math.max(
          finalSelectedTicket.minOrder,
          Math.min(effectiveMax, numValue)
        );
      } else {
        processedValue = Math.max(1, numValue);
      }
    } else if (name === 'selectedScheduleId') {
      // When schedule changes, reset ticket selection
      processedValue = parseInt(value, 10);
      setFormData(prev => ({
        ...prev,
        [name]: processedValue,
        ticketType: '', // Reset ticket type
        quantity: 1 // Reset quantity to default
      }));
      setFinalSelectedTicket(null); // Reset final selected ticket

      // Clear ticket-related errors
      if (errors.ticketType || errors.quantity) {
        setErrors(prev => ({
          ...prev,
          ticketType: '',
          quantity: ''
        }));
      }
      return; // Return early to prevent double setting
    }

    setFormData({
      ...formData,
      [name]: processedValue
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.ticketType) newErrors.ticketType = 'Please select a ticket type';
    if (!formData.selectedScheduleId) newErrors.selectedScheduleId = 'Please select a schedule';
    if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms and conditions';

    // Validate quantity against ticket constraints
    if (finalSelectedTicket) {
      if (formData.quantity < finalSelectedTicket.minOrder) {
        newErrors.quantity = `Minimum order is ${finalSelectedTicket.minOrder} tickets`;
      } else if (formData.quantity > finalSelectedTicket.maxOrder) {
        newErrors.quantity = `Maximum order is ${finalSelectedTicket.maxOrder} tickets`;
      } else if (formData.quantity > finalSelectedTicket.available) {
        newErrors.quantity = `Only ${finalSelectedTicket.available} tickets available`;
      }
    }

    return newErrors;
  };

  const handleSubmit = async () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setProcessing(true);

      let payload = {
        ticketId: formData.ticketType,
        quantity: formData.quantity,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phone,
        paymentMethod: isSelectedTicketFree ? 'NONE' : (formData.paymentMethod === 'card' ? 'CREDIT_CARD' : 'BANK_ACCOUNT'),
        scheduleId: formData.selectedScheduleId,
      };

      let paymentResult = null;

      if (!isSelectedTicketFree) {
        if (formData.paymentMethod === 'card') {

          // Register payment intent with your backend
          const piResponse = await api.post('/v1/api/payment/register-payment-intent', {
            email: formData.email,
            amount: calculateTotal(),
            currency: event.currency,
            paymentIntentId: paymentIntentId,
            purpose: 'EVENT_TICKET_PURCHASE',
            targetId: event.uuid,
          });

          // Process card payment
          paymentResult = await stripeCheckoutRef.current?.processPayment();
          if (paymentResult?.error) throw paymentResult.error;
          if (!paymentResult.paymentIntent) throw new Error('Payment failed');

          payload.pi = piResponse.data.id;
        } else {
          const selectedAccount = bankAccounts.find(acc => acc.id === selectedBankAccount);

          // Amount in major currency (price is tax inclusive)
          const amountCents = (finalSelectedTicket?.price || 0) * formData.quantity;

          const bankResponse = await api.post(`/v1/api/payment/process-bank-payment-intent`,
                              {
                                email: formData.email,
                                amount: amountCents,
                                currency: event.currency,
                                targetId: event.uuid,
                                purpose: 'EVENT_TICKET_PURCHASE',
                                paymentType: 'BANK_ACCOUNT',
                                stripeBankAccountId: selectedAccount.stripeBankAccountId,
                                description: `Event registration: ${event.title} - ${finalSelectedTicket?.name}`
                              }
          );

          payload.pi = bankResponse.data;
        }
      }

      const response = await api.post(`/v1/api/events/${event.id}/tickets/purchase`, payload);
      setPurchaseResponse(response.data);
      setProcessing(false);

      // Move to confirmation step
      if (isSelectedTicketFree) {
        setActiveStep(1); // Confirmation step for free tickets
      } else {
        setActiveStep(2); // Confirmation step for paid tickets
      }
    } catch (error) {
      console.error('Error processing registration:', error);
      setErrors({
        general: error.response?.data || error.message || 'Registration failed'
      });
      setProcessing(false);
    }
  };

  const calculateTotal = () => {
    return (finalSelectedTicket?.price || 0) * formData.quantity;
  };

  const isPaymentValid = () => {
    if (isSelectedTicketFree) return true;

    if (formData.paymentMethod === 'card') {
      return validateEmail(formData.email) && clientSecret;// && cardPaymentReady;
    }

    if (formData.paymentMethod === 'bank_account') {
      return bankAccounts.length > 0 && !!selectedBankAccount;
    }

    return false;
  };

  const handleConnectBank = () => {
    onClose();
    alert('Redirecting to bank connection setup...');
  };

  // Helper function to format schedule display
  const formatScheduleDisplay = (schedule) => {
    try {
      // Parse date components to avoid UTC conversion issues
      const [year, month, day] = schedule.date.split('-').map(Number);

      // Create date using local components (month is 0-indexed in JS)
      const dateObj = new Date(year, month - 1, day);

      // Format the date (will use browser's locale)
      const formattedDate = dateObj.toLocaleDateString();

      // Format times
      const formatTimeString = (timeStr) => {
        if (!timeStr) return '';
        const [hours, minutes] = timeStr.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        return `${hour12}:${minutes} ${ampm}`;
      };

      const startTimeFormatted = formatTimeString(schedule.startTime);
      const endTimeFormatted = formatTimeString(schedule.endTime);

      // Get timezone abbreviation
      const timezoneAbbr = getTimezoneAbbreviation(schedule.timeZone);
      const timezoneDisplay = timezoneAbbr ? ` (${timezoneAbbr})` : '';

      return `${formattedDate} ${startTimeFormatted} - ${endTimeFormatted}${timezoneDisplay}`;
    } catch (error) {
      console.error('Error formatting schedule display:', error);
      return `${schedule.date} ${schedule.startTime} - ${schedule.endTime}`;
    }
  };

  const renderRegistrationInfo = () => (
    <Box>
      <Typography variant="body1" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
        Attendee Information
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="First Name"
            name="firstName"
            value={formData.firstName ? formData.firstName.charAt(0).toUpperCase() + formData.firstName.slice(1).toLowerCase() : ''}
            onChange={handleChange}
            error={!!errors.firstName}
            helperText={errors.firstName}
            disabled={!!user}
            size='small'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Last Name"
            name="lastName"
            value={formData.lastName ? formData.lastName.charAt(0).toUpperCase() + formData.lastName.slice(1).toLowerCase() : ''}
            onChange={handleChange}
            error={!!errors.lastName}
            helperText={errors.lastName}
            disabled={!!user}
            size='small'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Email Address"
            name="email"
            type="email"
            value={formData.email?.toLowerCase() || ''}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            disabled={!!user}
            size='small'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Phone Number"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            error={!!errors.phone}
            helperText={errors.phone}
            disabled={!!user}
            size='small'
          />
        </Grid>
      </Grid>

      <Typography variant="body1" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
        Date Selection
      </Typography>
      <Typography variant="body2" gutterBottom sx={{ mt: 0 }}>
        Select a date to see available tickets
      </Typography>

      <FormControl component="fieldset" fullWidth error={!!errors.selectedScheduleId}>
        <RadioGroup
          name="selectedScheduleId"
          value={formData.selectedScheduleId}
          onChange={handleChange}
        >
          {event?.schedules?.filter(schedule => schedule.isCurrent).map((schedule) => (
            <Box
              key={schedule.id}
              sx={{
                border: formData.selectedScheduleId === schedule.id ? '2px solid' : '1px solid',
                borderColor: formData.selectedScheduleId === schedule.id
                  ? 'primary.main'
                  : errors.selectedScheduleId
                    ? 'error.main'
                    : 'divider',
                borderRadius: 1,
                p: { xs: 0.5, sm: 1 },
                mb: { xs: 1, sm: 2 },
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'action.hover'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <FormControlLabel
                  value={schedule.id}
                  control={<Radio size={isSmallScreen ? 'small' : 'medium'} />}
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontSize: isSmallScreen ? '0.95rem' : '1rem' }}>
                        {formatScheduleDisplay(schedule)}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Box>
          ))}

          {/* Other schedules (in accordion) */}
          {event?.schedules?.filter(schedule => !schedule.isCurrent).length > 0 && (
            <Accordion sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  View Other Available Dates ({event.schedules.filter(schedule => !schedule.isCurrent).length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {event.schedules.filter(schedule => !schedule.isCurrent).map((schedule) => (
                    <Grid item xs={12} sm={6} md={4} key={schedule.id}>
                      <Box
                        sx={{
                          border: formData.selectedScheduleId === schedule.id ? '2px solid' : '1px solid',
                          borderColor: formData.selectedScheduleId === schedule.id
                            ? 'primary.main'
                            : errors.selectedScheduleId
                              ? 'error.main'
                              : 'divider',
                          borderRadius: 1,
                          p: 1,
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                        onClick={(e) => {
                          // Check if the click was on the radio button specifically
                          const isRadioClick = e.target.type === 'radio' ||
                            e.target.closest('input[type="radio"]') ||
                            e.target.closest('span[role="radio"]');

                          if (!isRadioClick) {
                            handleChange({ target: { name: 'selectedScheduleId', value: schedule.id } });
                          }
                        }}
                      >
                        <FormControlLabel
                          value={schedule.id}
                          control={
                            <Radio
                              size={isSmallScreen ? 'small' : 'medium'}
                              checked={formData.selectedScheduleId === schedule.id}
                            />
                          }
                          label={
                            <Typography variant="body1">
                              {formatScheduleDisplay(schedule)}
                            </Typography>
                          }
                          sx={{
                            width: '100%',
                            margin: 0,
                            alignItems: 'center',
                            pointerEvents: 'none' // This makes the label not capture clicks
                          }}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}
        </RadioGroup>

        {errors.selectedScheduleId && (
          <Typography variant="caption" color="error" sx={{ ml: 2, mt: 1, display: 'block' }}>
            {errors.selectedScheduleId}
          </Typography>
        )}
      </FormControl>



      <Typography variant="body1" gutterBottom fontWeight="bold" >
        Ticket Selection
      </Typography>

      {!formData.selectedScheduleId ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Please select an event date first to see available tickets.
        </Alert>
      ) : selectedSchedule?.tickets?.length === 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No tickets available for the selected date.
        </Alert>
      ) : (
        <FormControl component="fieldset" fullWidth error={!!errors.ticketType}>
          <RadioGroup
            name="ticketType"
            value={formData.ticketType}
            onChange={handleChange}
          >
            {selectedSchedule?.tickets?.map((ticket) => (
              <Box
                key={ticket.id}
                sx={{
                  border: formData.ticketType === ticket.id ? '2px solid' : '1px solid',
                  borderColor: formData.ticketType === ticket.id
                    ? 'primary.main'
                    : errors.ticketType
                      ? 'error.main'
                      : 'divider',
                  borderRadius: 1,
                  p: 1,
                  mb: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  opacity: ticket.isActive && ticket.isOnSale ? 1 : 0.6
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <FormControlLabel
                    value={ticket.id}
                    control={<Radio size="small" />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: isSmallScreen ? '0.85rem' : '0.9rem', fontWeight: 500 }}>
                          {ticket.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: isSmallScreen ? '0.75rem' : '0.8rem' }}>
                          {ticket.description}
                        </Typography>
                        {!ticket.isActive && (
                          <Chip label="Not Active" size="small" color="default" sx={{ mt: 0.5, height: 18, fontSize: '0.7rem' }} />
                        )}
                        {!ticket.isOnSale && (
                          <Chip label="Not Available" size="small" color="default" sx={{ mt: 0.5, ml: 0.5, height: 18, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    }
                    disabled={!ticket.isActive || !ticket.isOnSale}
                  />
                </Box>
                <Typography
                  variant="body1"
                  color={ticket.price === 0 ? "success.main" : "primary"}
                  sx={{ fontSize: isSmallScreen ? '0.9rem' : '1rem', fontWeight: 600 }}
                >
                  {ticket.price === 0 ? 'Free' : `${getCurrencySymbol(event.currency)}${ticket.price}`}
                </Typography>
              </Box>
            ))}
          </RadioGroup>
          {errors.ticketType && (
            <Typography variant="caption" color="error" sx={{ ml: 2 }}>
              {errors.ticketType}
            </Typography>
          )}
        </FormControl>
      )}

      {formData.ticketType && (() => {
        const effectiveMax = finalSelectedTicket
          ? Math.min(
            finalSelectedTicket.maxOrder || 10,
            finalSelectedTicket.available || finalSelectedTicket.maxOrder || 10
          )
          : 10;
        const minOrder = finalSelectedTicket?.minOrder || 1;

        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom sx={{ fontSize: isSmallScreen ? '0.95rem' : '1rem' }}>
              Quantity (Min: {minOrder}, Max: {effectiveMax})
            </Typography>
            <TextField
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              error={!!errors.quantity}
              helperText={errors.quantity}
              inputProps={{
                min: minOrder,
                max: effectiveMax
              }}
              sx={{ width: 100 }}
              size='small'
            />
            {isSelectedTicketFree && (
              <Alert severity="success" sx={{ mt: 2 }}>
                This is a free ticket! No payment required. Click "Complete Registration" to finish.
              </Alert>
            )}
          </Box>
        );
      })()}

      {/* Terms and Conditions Checkbox */}
      <Box sx={{ mt: { xs: 2, sm: 3 }, p: { xs: 1.5, sm: 2 }, border: '1px solid', borderColor: errors.agreeToTerms ? 'error.main' : 'divider', borderRadius: 1 }}>
        <FormControl error={!!errors.agreeToTerms} component="fieldset" fullWidth>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.agreeToTerms}
                onChange={handleChange}
                name="agreeToTerms"
                color={errors.agreeToTerms ? "error" : "primary"}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: isSmallScreen ? '0.9rem' : '1rem' }}>
                I agree to the{' '}
                <Link
                  href="/legal/documents/terms-and-conditions"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontWeight: 'bold' }}
                >
                  Terms and Conditions
                </Link>
                {' '}and{' '}
                <Link
                  href="/legal/documents/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontWeight: 'bold' }}
                >
                  Privacy Policy
                </Link>
              </Typography>
            }
          />
          {errors.agreeToTerms && (
            <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5, display: 'block' }}>
              {errors.agreeToTerms}
            </Typography>
          )}
        </FormControl>
      </Box>
    </Box>
  );

  const renderPaymentInfo = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
        Payment Information
      </Typography>

      <Box sx={{ mb: { xs: 2, sm: 3 }, p: { xs: 1.5, sm: 2 }, borderRadius: 1 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
          Order Summary
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
            {formatScheduleDisplay(selectedSchedule)} - {finalSelectedTicket?.name} × {formData.quantity}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
            {getCurrencySymbol(event.currency)}{((finalSelectedTicket?.price || 0) * formData.quantity).toFixed(2)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
            Tax
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
            Included
          </Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body1" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
            Total
          </Typography>
          <Typography variant="body1" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
            {getCurrencySymbol(event.currency)}{calculateTotal().toFixed(2)}
          </Typography>
        </Box>
      </Box>

      <FormControl component="fieldset" fullWidth>
        <FormLabel component="legend" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' }, mb: 1 }}>Payment Method</FormLabel>
        <RadioGroup
          name="paymentMethod"
          value={formData.paymentMethod}
          onChange={handleChange}
          row
        >
          {/* Bank Account Option - Only for USD */}
          {user?.defaultCurrency?.toUpperCase() === 'USD' && (
            <FormControlLabel
              value="bank_account"
              control={<Radio size={isSmallScreen ? 'small' : 'medium'} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <BankIcon sx={{ mr: 1 }} />
                  <Typography sx={{ fontSize: isSmallScreen ? '0.95rem' : '1rem' }}>Bank Transfer</Typography>
                </Box>
              }
              sx={{ mr: { xs: 2, sm: 4 } }}
            />
          )}

          {/* Credit Card Option */}
          <FormControlLabel
            value="card"
            control={<Radio size={isSmallScreen ? 'small' : 'medium'} />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PaymentIcon sx={{ mr: 1 }} />
                <Typography sx={{ fontSize: isSmallScreen ? '0.95rem' : '1rem' }}>Credit/Debit Card</Typography>
                {creatingPaymentIntent && (
                  <CircularProgress size={16} sx={{ ml: 1 }} />
                )}
              </Box>
            }
            disabled={creatingPaymentIntent}
          />
        </RadioGroup>

        {formData.paymentMethod === 'card' && (
          <Box sx={{ ml: isSmallScreen ? 2 : 4, mt: 1 }}>
            {creatingPaymentIntent ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" sx={{ ml: 1 }}>Setting up payment...</Typography>
              </Box>
            ) : clientSecret ? (
              <>
                <StripeCheckout
                  ref={stripeCheckoutRef}
                  clientSecret={clientSecret}
                  email={formData.email}
                  onError={(error) => {
                    setErrors({ general: error.message });
                    setCardPaymentReady(false);
                  }}
                  onReady={() => setCardPaymentReady(true)}
                />
                {errors.general && (
                  <Typography color="error" variant="body2" sx={{ mt: 1, fontSize: isSmallScreen ? '0.85rem' : '0.95rem' }}>
                    {errors.general}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Enter your card details to complete payment
              </Typography>
            )}
          </Box>
        )}
      </FormControl>

      {formData.paymentMethod === 'bank_account' && (
        <Box sx={{ mt: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            Bank Information
          </Typography>
          {fetchingAccounts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" sx={{ ml: 1 }}>Loading bank accounts...</Typography>
            </Box>
          ) : bankAccounts.length > 0 ? (
            <FormControl fullWidth error={!!errors.bankAccount}>
              <InputLabel>Select Bank Account</InputLabel>
              <Select
                value={selectedBankAccount}
                onChange={(e) => setSelectedBankAccount(e.target.value)}
                label="Select Bank Account"
              >
                {bankAccounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BankIcon sx={{ mr: 2 }} />
                      {account.bankName} •••• •••• •••• {account.last4}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {errors.bankAccount && (
                <FormHelperText>{errors.bankAccount}</FormHelperText>
              )}
            </FormControl>
          ) : (
            <Alert severity="warning">
              No bank accounts found. Please add a bank account in your account settings.
            </Alert>
          )}
        </Box>
      )}

      {errors.general && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: isSmallScreen ? '0.9rem' : '1rem' }}>
            {errors.general}
          </Typography>
        </Alert>
      )}
    </Box>
  );

  const renderConfirmation = () => (
    <Box sx={{ textAlign: 'center', py: { xs: 1.5, sm: 2 } }}>
      <Box sx={{ color: 'success.main', fontSize: { xs: 48, sm: 60 }, mb: { xs: 1.5, sm: 2 } }}>
        ✓
      </Box>
      <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
        Registration Successful!
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
        Thank you for registering for <strong>{event?.title}</strong>.
        A confirmation email has been sent to <strong>{formData.email}</strong>.
      </Typography>

      <Box sx={{ p: { xs: 1.5, sm: 2 }, border: '1px dashed', borderColor: 'divider', borderRadius: 1, mb: { xs: 1.5, sm: 2 } }}>
        <Typography variant="body2" gutterBottom sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
          <strong>Registration ID:</strong> {purchaseResponse?.purchaseId}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
          <strong>Event Date:</strong> {selectedSchedule ? formatScheduleDisplay(selectedSchedule) : 'N/A'}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
          <strong>Date:</strong> {new Date().toLocaleDateString()}
        </Typography>
        {!isSelectedTicketFree && (
          <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
            <strong>Amount Paid:</strong> {getCurrencySymbol(event.currency)}{purchaseResponse?.totalAmount?.toFixed(2)}
          </Typography>
        )}
      </Box>

      <Alert severity="info">
        <Typography sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
          Please check your email for your tickets and event details.
          Add the event to your calendar to avoid missing it!
        </Typography>
      </Alert>
    </Box>
  );

  const renderStepContent = (step) => {
    if (isSelectedTicketFree) {
      switch (step) {
        case 0: return renderRegistrationInfo();
        case 1: return renderConfirmation();
        default: return <Typography>Unknown step</Typography>;
      }
    } else {
      switch (step) {
        case 0: return renderRegistrationInfo();
        case 1: return renderPaymentInfo();
        case 2: return renderConfirmation();
        default: return <Typography>Unknown step</Typography>;
      }
    }
  };

  const getNextButtonText = () => {
    if (processing) return 'Processing...';

    if (isSelectedTicketFree) {
      if (activeStep === 0) return 'Complete Registration';
    } else {
      if (activeStep === 1) return 'Complete Registration';
    }

    return 'Continue';
  };

  const isNextButtonDisabled = () => {
    if (processing) return true;

    if (activeStep === 0) {
      return !formData.ticketType || !formData.agreeToTerms || Object.keys(validateForm()).length > 0;
    }

    if (activeStep === 1 && !isSelectedTicketFree) {
      return !isPaymentValid();
    }

    return false;
  };

  const handleButtonClick = () => {
    if (activeStep === 0 && isSelectedTicketFree) {
      handleSubmit();
    } else if (activeStep === 1 && !isSelectedTicketFree) {
      handleSubmit();
    } else {
      handleNext();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isSmallScreen}
      scroll="paper"
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: isSmallScreen ? '100%' : '1000px',
          margin: isSmallScreen ? 0 : 3,
          borderRadius: isSmallScreen ? 0 : 2
        }
      }}
    >
      <DialogTitle
        sx={{
          p: isSmallScreen ? 2 : 3,
          pb: 1,
          backgroundColor: theme.palette.primary.main,
          color: 'white'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="h6"
            sx={{
              color: 'primary.contrastText',
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}
          >
            Register for Event
          </Typography>
          <IconButton
            onClick={onClose}
            size={isSmallScreen ? 'small' : 'medium'}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>


      </DialogTitle>

      <DialogContent dividers sx={{ p: isSmallScreen ? 2 : 3 }}>
        <Box sx={{ mb: 3 }}>
          <Stepper
            activeStep={activeStep}
            sx={{
              '& .MuiStepLabel-label': {
                display: isMediumScreen ? 'none' : 'block',
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                fontWeight: 500
              },
              '& .MuiStepIcon-root': {
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                color: 'action.disabled',
                '&.Mui-active': {
                  color: 'primary.main'
                },
                '&.Mui-completed': {
                  color: 'success.main'
                }
              },
              '& .MuiStepIcon-text': {
                fill: 'white',
                fontWeight: 'bold',
                fontSize: '0.75rem'
              },
              '& .MuiStepConnector-line': {
                borderColor: 'divider',
                borderTopWidth: 1.5
              },
              '& .Mui-completed + .MuiStepConnector-root .MuiStepConnector-line': {
                borderColor: 'success.main'
              }
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>
                  {!isMediumScreen && label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>


        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions sx={{ p: isSmallScreen ? 1.5 : 2 }}>
        {activeStep > 0 && activeStep < steps.length - 1 && (
          <Button
            onClick={handleBack}
            disabled={processing}
            size="small"
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              px: { xs: 1.5, sm: 2 },
              py: 0.5
            }}
          >
            Back
          </Button>
        )}

        {activeStep < steps.length - 1 ? (
          <Button
            onClick={handleButtonClick}
            variant="contained"
            disabled={isNextButtonDisabled()}
            size="small"
            startIcon={processing ? <CircularProgress size={isSmallScreen ? 14 : 16} /> : null}
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              px: { xs: 2, sm: 2.5 },
              py: 0.5,
              fontWeight: 600
            }}
          >
            {getNextButtonText()}
          </Button>
        ) : (
          <Button
            onClick={onClose}
            variant="contained"
            size="small"
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              px: { xs: 2, sm: 2.5 },
              py: 0.5,
              fontWeight: 600
            }}
          >
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EventRegistrationModal;